import React from 'react';
import { useTranslation } from 'react-i18next';
import { FolderStatusEnum } from '@koob/enums';
import cn from '../../utils/cn';

// Dot + border share one hue per status; the border is drawn at full strength
// when the pill is active and faded when it is not.
const STATUS_COLOR = {
  [FolderStatusEnum.NEW]: { dot: 'bg-gray-400', border: 'border-gray-400' },
  [FolderStatusEnum.DRAFT]: { dot: 'bg-amber-400', border: 'border-amber-400' },
  [FolderStatusEnum.CONFIRMING]: {
    dot: 'bg-orange-400',
    border: 'border-orange-400',
  },
  [FolderStatusEnum.CONFIRMED]: {
    dot: 'bg-green-400',
    border: 'border-green-400',
  },
  [FolderStatusEnum.ON_SPOT]: { dot: 'bg-blue-400', border: 'border-blue-400' },
  [FolderStatusEnum.COMPLETED]: {
    dot: 'bg-purple-400',
    border: 'border-purple-400',
  },
  [FolderStatusEnum.CANCELLED]: { dot: 'bg-red-400', border: 'border-red-400' },
  [FolderStatusEnum.ARCHIVED]: {
    dot: 'bg-gray-700',
    border: 'border-gray-700',
  },
};

// Status order + the AC label keys (Ongoing maps to CONFIRMING).
const STATUS_ORDER = [
  FolderStatusEnum.NEW,
  FolderStatusEnum.DRAFT,
  FolderStatusEnum.CONFIRMING,
  FolderStatusEnum.CONFIRMED,
  FolderStatusEnum.ON_SPOT,
  FolderStatusEnum.COMPLETED,
  FolderStatusEnum.CANCELLED,
  FolderStatusEnum.ARCHIVED,
];

// KD-3842: "All" is the default view, which leaves archived folders out.
const ACTIVE_STATUSES = STATUS_ORDER.filter(
  (status) => status !== FolderStatusEnum.ARCHIVED,
);

const STATUS_LABEL_KEY = {
  [FolderStatusEnum.NEW]: 'new',
  [FolderStatusEnum.DRAFT]: 'draft',
  [FolderStatusEnum.CONFIRMING]: 'ongoing',
  [FolderStatusEnum.CONFIRMED]: 'confirmed',
  [FolderStatusEnum.ON_SPOT]: 'onSpot',
  [FolderStatusEnum.COMPLETED]: 'completed',
  [FolderStatusEnum.CANCELLED]: 'cancelled',
  [FolderStatusEnum.ARCHIVED]: 'archived',
};

// A constant border-2 width is always drawn (color/opacity is the only thing
// that changes between selected/deselected) so the card never resizes on click.
function StatusCard({
  dot,
  border,
  label,
  count,
  active,
  onClick,
}: {
  dot?: string;
  border: string;
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <div className="relative group shrink-0">
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'flex w-full flex-col items-center gap-1 rounded-lg border-2 border-solid px-4 py-3 transition-all hover:shadow-sm',
          dot ? 'min-w-[100px]' : 'min-w-[80px]',
          active
            ? cn('bg-white shadow-sm', border)
            : cn('bg-white/60 hover:bg-white', border, 'border-opacity-30'),
        )}
      >
        <span className="flex items-center gap-1.5">
          {dot && <span className={cn('h-2 w-2 rounded-full shrink-0', dot)} />}
          <span className="text-xs font-medium text-k-p whitespace-nowrap">
            {label}
          </span>
        </span>
        <span className="text-2xl font-bold text-k-h tabular-nums">
          {count}
        </span>
      </button>
    </div>
  );
}

// Multi-select status filter: clicking a card toggles that status in the shared
// selected set (identical behavior for the list and kanban views). The "All"
// card selects every status; the tag row below mirrors the current selection.
export default function FolderStatusFilters({
  counts = {},
  selectedStatuses = [],
  onToggle,
  onSelectAll,
}: {
  // keyed by status, plus `all` for the total
  counts?: Record<string, number>;
  selectedStatuses?: string[];
  onToggle?: (status: string) => void;
  onSelectAll?: () => void;
}) {
  const { t } = useTranslation('folders');
  const allSelected =
    ACTIVE_STATUSES.every((status) => selectedStatuses.includes(status)) &&
    !selectedStatuses.includes(FolderStatusEnum.ARCHIVED);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-3 overflow-x-auto pb-1">
        <StatusCard
          border="border-k-orange"
          label={t('statusFilters.all')}
          count={counts.all ?? 0}
          active={allSelected}
          onClick={() => onSelectAll?.()}
        />

        {STATUS_ORDER.map((status) => (
          <StatusCard
            key={status}
            dot={STATUS_COLOR[status].dot}
            border={STATUS_COLOR[status].border}
            label={t(`statusFilters.${STATUS_LABEL_KEY[status]}`)}
            count={counts[status] ?? 0}
            active={selectedStatuses.includes(status)}
            onClick={() => onToggle?.(status)}
          />
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">
          {t('statusFilters.filtering')}
        </span>
        {STATUS_ORDER.filter((status) => selectedStatuses.includes(status)).map(
          (status) => (
            <span
              key={status}
              className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-foreground"
            >
              <i
                className="fas fa-check text-[10px] text-brand"
                aria-hidden="true"
              />
              {t(`statusFilters.${STATUS_LABEL_KEY[status]}`)}
              <button
                type="button"
                onClick={() => onToggle?.(status)}
                aria-label={t('removeFilter')}
                className="ml-0.5 cursor-pointer border-none bg-transparent text-muted-foreground hover:text-foreground"
              >
                ×
              </button>
            </span>
          ),
        )}
        {!allSelected && (
          <button
            type="button"
            onClick={() => onSelectAll?.()}
            className="cursor-pointer border-none bg-transparent text-xs text-brand hover:underline"
          >
            {t('statusFilters.showAll')}
          </button>
        )}
      </div>
    </div>
  );
}
